
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { collectErrors, errorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  identityIntegrationsService,
  IIdentityAttributeMapping,
  IIdentityIntegrationAuditEntry,
  isSubjectSource,
} from '@/services/identityIntegrationsService';

interface IAuditChange {
  field: string;
  from: string | null;
  to: string;
}

const HIDDEN_FIELDS = ['id', 'uuid', 'created_at', 'updated_at'];

// Recorded as one object per change (sp-identity ADR-0017); shown as one row
// per sub-field so a scope edit does not read as "the configuration changed".
// The client secret inside it is only ever "set" or "replaced", never a value.
const EXPANDED_FIELDS = ['oidc'];
const FINGERPRINT_FIELDS = ['idp_certificate'];
const FINGERPRINT_PREVIEW_LENGTH = 16;

// Stored values that have a name in the interface: the status reads "Active",
// not "active", and a credential reads "Set", never as the marker it is
// recorded with.
const VALUE_LABEL_PREFIXES: { [field: string]: string } = {
  status: 'identityIntegrations.status.',
  protocol: 'identityIntegrations.protocols.',
  community_resolution_mode: 'identityIntegrations.communityResolution.modes.',
  portal_pin: 'identityIntegrations.audit.credentialStates.',
  'oidc.client_secret': 'identityIntegrations.audit.credentialStates.',
};

@Component({
  name: 'AuditLogPanel',
  components: { Datim, PaginationBar },
})
export default class AuditLogPanel extends Vue {
  @Prop({ required: true })
  protected integrationId!: number;

  protected loading: boolean = true;
  protected entries: IIdentityIntegrationAuditEntry[] = [];
  protected currentPage: number = 1;
  protected total: number = 0;
  protected perPage: number = 15;
  protected action: string | null = null;
  protected loadError: string | null = null;
  readonly perPageOptions: number[] = [15, 25, 50];

  created() {
    this.fetchPage(1);
  }

  get lastPage(): number {
    return Math.max(1, Math.ceil(this.total / (this.perPage || 15)));
  }

  get actionOptions() {
    return [
      { text: this.$t('identityIntegrations.audit.actions.insert') as string, value: 'insert' },
      { text: this.$t('identityIntegrations.audit.actions.update') as string, value: 'update' },
      { text: this.$t('identityIntegrations.audit.actions.delete') as string, value: 'delete' },
    ];
  }

  actionLabel(action: string): string {
    const key = 'identityIntegrations.audit.actions.' + action;

    return this.$te(key) ? (this.$t(key) as string) : action;
  }

  actorLabel(entry: IIdentityIntegrationAuditEntry): string {
    if (entry.changed_by_name) {
      return entry.changed_by_name;
    }
    if (entry.user_id) {
      return '#' + entry.user_id;
    }

    return this.$t('identityIntegrations.audit.unknownActor') as string;
  }

  fieldLabel(field: string): string {
    const key = 'identityIntegrations.auditFields.' + field;

    return this.$te(key) ? (this.$t(key) as string) : field;
  }

  changesOf(entry: IIdentityIntegrationAuditEntry): IAuditChange[] {
    const next = this.expanded(entry.new_data || {});
    const previous = this.expanded(entry.old_data || {});
    const changes: IAuditChange[] = [];

    Object.keys(next).forEach((field) => {
      if (HIDDEN_FIELDS.indexOf(field) !== -1) {
        return;
      }

      const from = entry.old_data && field in previous ? this.display(field, previous[field]) : null;
      const to = this.display(field, next[field]);

      if (from === to) {
        return;
      }

      changes.push({ field, from, to });
    });

    return changes;
  }

  private expanded(data: { [key: string]: any }): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    Object.keys(data).forEach((field) => {
      const value = data[field];

      if (EXPANDED_FIELDS.indexOf(field) !== -1 && value && typeof value === 'object' && !Array.isArray(value)) {
        Object.keys(value).forEach((subField) => {
          result[field + '.' + subField] = value[subField];
        });
      } else {
        result[field] = value;
      }
    });

    return result;
  }

  private display(field: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return this.$t('identityIntegrations.audit.none') as string;
    }

    const labelKey = VALUE_LABEL_PREFIXES[field] && typeof value === 'string' ? VALUE_LABEL_PREFIXES[field] + value : null;

    if (labelKey && this.$te(labelKey)) {
      return this.$t(labelKey) as string;
    }

    if (FINGERPRINT_FIELDS.indexOf(field) !== -1) {
      return String(value).substring(0, FINGERPRINT_PREVIEW_LENGTH) + '…';
    }

    // The two structured values the backend records, written the way an
    // operator reads them rather than as "[object Object]".
    if (field === 'attribute_mappings' && Array.isArray(value)) {
      return value.length
        ? value.map((mapping: IIdentityAttributeMapping) => this.mappingLabel(mapping)).join('; ')
        : (this.$t('identityIntegrations.audit.none') as string);
    }

    if (field === 'community_mapping' && typeof value === 'object') {
      return value.external_value + ' → #' + value.community_id;
    }

    if (Array.isArray(value)) {
      return value.length
        ? value.map((item: any) => (item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ')
        : (this.$t('identityIntegrations.audit.none') as string);
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    if (typeof value === 'boolean') {
      return value ? (this.$t('common.yes') as string) : (this.$t('common.no') as string);
    }

    if (value === 1 || value === 0) {
      return value === 1 ? (this.$t('common.yes') as string) : (this.$t('common.no') as string);
    }

    return String(value);
  }

  async fetchPage(page: number) {
    this.loading = true;
    this.loadError = null;
    try {
      const result = await identityIntegrationsService.auditLog(this.integrationId, page, this.perPage, this.action);
      this.entries = result.data;
      this.currentPage = result.page;
      this.total = result.total;
      this.perPage = result.per_page;
    } catch (error) {
      this.entries = [];
      this.loadError = errorMessage(this, collectErrors(error)[0]);
    } finally {
      this.loading = false;
    }
  }

  private mappingLabel(mapping: IIdentityAttributeMapping): string {
    const field = 'identityIntegrations.canonicalFields.' + mapping.canonical_field;
    const source = isSubjectSource(mapping.source_type)
      ? (this.$t('identityIntegrations.audit.protocolSubject') as string)
      : mapping.source_name;

    return (this.$te(field) ? this.$t(field) : mapping.canonical_field) + ' ← ' + source;
  }

  onPerPageChange(perPage: number) {
    this.perPage = perPage;
    this.fetchPage(1);
  }
}
